import React from 'react';
import { Form, Field } from 'react-final-form';
import { Typography, Button, CircularProgress, Box } from '@mui/material';
import i18next from 'i18next';

import Input from 'components/inputs/Input';
import { requiredValidation, emailValidation } from 'helpers/validations';

import { IForgotPasswordViewProps } from './types';
import { ForgotPasswordContainer, StyledForgotPaper, FormWrapper } from './styles';
import './i18n';

const ForgotPasswordView: React.FC<IForgotPasswordViewProps> = ({
  onSubmit,
  loading,
  success,
  error,
}) => {
  const validate = (values: { email: string }) => {
    const errors: any = {};
    errors.email = requiredValidation(values.email) || emailValidation(values.email);
    return errors;
  };

  if (success) {
    return (
      <ForgotPasswordContainer>
        <StyledForgotPaper elevation={0} sx={{ textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            {i18next.t<string>('FORGOT_PASSWORD:SUCCESS_TITLE')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 300 }}>
            {i18next.t<string>('FORGOT_PASSWORD:SUCCESS_MESSAGE')}
          </Typography>
        </StyledForgotPaper>
      </ForgotPasswordContainer>
    );
  }

  return (
    <ForgotPasswordContainer>
      <StyledForgotPaper elevation={0}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, letterSpacing: '-0.02em' }}>
          {i18next.t<string>('FORGOT_PASSWORD:FORM_TITLE')}
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 300, mb: 1 }}>
          {i18next.t<string>('FORGOT_PASSWORD:FORM_SUBTITLE')}
        </Typography>

        <FormWrapper>
          <Form
            onSubmit={(values) => onSubmit(values.email)}
            validate={validate}
            render={({ handleSubmit, pristine, invalid }) => (
              <form onSubmit={handleSubmit}>
                <Box sx={{ mb: 4 }}>
                  <Field name="email">
                    {({ input, meta }) => (
                      <Input
                        input={input}
                        meta={meta}
                        label={i18next.t<string>('FORGOT_PASSWORD:EMAIL_LABEL')}
                      />
                    )}
                  </Field>
                </Box>

                {error && (
                  <Typography color="error" variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
                    {error}
                  </Typography>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading || pristine || invalid}
                  sx={{
                    py: 1.8,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderRadius: '12px',
                    boxShadow: 'none',
                    backgroundColor: '#0061ff',
                    '&:hover': { backgroundColor: '#0052db', boxShadow: 'none' },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    i18next.t<string>('FORGOT_PASSWORD:SUBMIT_BUTTON')
                  )}
                </Button>
              </form>
            )}
          />
        </FormWrapper>
      </StyledForgotPaper>
    </ForgotPasswordContainer>
  );
};

export default ForgotPasswordView;
